import React from 'react'
import {
  faFacebookSquare,
  faInstagram,
} from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Col, Row } from 'antd'
import { useMarketContext } from 'contexts/MarketContext'
import { i18n, useTranslation } from 'lib/i18n'
import dynamic from 'next/dynamic'

import Link from 'components/Link/Link'
import LogoImage from 'components/Logotype/LogoImage'
import MarketSwitcher from 'components/MarketSwitcher/MarketSwitcher'
import Wrapper from 'components/Wrapper/Wrapper'
import useLanguage from 'hooks/useLanguage'

import styles from './BusinessPageFooter.module.css'

const NewsletterSignup = dynamic(
  () => import('components/NewsletterSignup/NewsletterSignup'),
)

const BusinessPageFooter: React.FC = () => {
  const { t } = useTranslation()
  const { market, detectedMarket } = useMarketContext()
  const language = useLanguage()

  const pickedMarket = detectedMarket
    ? `
      Market: ${detectedMarket.market},
      Lang: ${detectedMarket.language},
      i18n: ${i18n.language}
      Detected by: ${detectedMarket.detectedBy},
      Suggested by: ${detectedMarket.suggestedBy}
    `
    : null

  const marketId = market.selectedMarket.id
  const { instagram, facebook, blogPage } = market.selectedMarket?.config || {}
  const blogPageLink = blogPage && (blogPage as string)[language.code]

  return (
    <footer className={styles.base} data-cy="BusinessPageFooter">
      <Wrapper>
        <div className={styles.wrapper}>
          <Row justify="space-between">
            <Col xs={24} md={10} lg={8}>
              <section className={styles.sectionNewsletter}>
                <NewsletterSignup />
              </section>
            </Col>

            <Col xs={24} md={10} lg={8}>
              <section className={styles.footerSection}>
                <nav>
                  <Link href="/about">
                    <a className={styles.navLink}>
                      {t('footer.aboutUs', 'About Us')}
                    </a>
                  </Link>
                  <Link href="/values">
                    <a className={styles.navLink}>
                      {t('footer.values', 'Our Values')}
                    </a>
                  </Link>
                  {marketId === 'de' && (
                    <Link href="/impressum">
                      <a className={styles.navLink}>Impressum</a>
                    </Link>
                  )}
                  {blogPageLink && (
                    <Link href={blogPageLink}>
                      <a className={styles.navLink}>
                        {t('footer.blog', 'Blog')}
                      </a>
                    </Link>
                  )}
                  <Link href="/faq">
                    <a className={styles.navLink}>
                      {t('footer.helpAndContact', 'Help & Contact')}
                    </a>
                  </Link>
                </nav>
              </section>
            </Col>
          </Row>
          <Row
            align="middle"
            justify="space-between"
            className={styles.lineItem}
          >
            <Col span={4} xs={12}>
              <nav>
                <a
                  className={styles.socialLink}
                  href={`https://www.instagram.com/${instagram}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
                <a
                  className={styles.socialLink}
                  href={`https://www.facebook.com/${facebook}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FontAwesomeIcon icon={faFacebookSquare} />
                </a>
              </nav>
            </Col>
            <Col span={4} xs={12}>
              <MarketSwitcher showText />
            </Col>
          </Row>
          <Row
            align="middle"
            justify="space-between"
            className={styles.lineItem}
          >
            <Col span={4} xs={9}>
              <Link href="/">
                <a>
                  <LogoImage alt="memmo logotype" size="xs" variant="light" />
                </a>
              </Link>
            </Col>
            <Col span={4} xs={15}>
              <div className={styles.copyright}>
                <span title={pickedMarket}>&copy;</span> 2019 -{' '}
                {new Date().getFullYear()} Meetly AB
              </div>
            </Col>
          </Row>

          <Row className={styles.lineItem}>
            <nav className={styles.miscLinks}>
              <Link href="/privacy-policy">
                <a className={styles.miscLink}>
                  {t('footer.privacyPolicy', 'Privacy Policy')}
                </a>
              </Link>
              <Link href="/terms-of-service">
                <a className={styles.miscLink}>
                  {t('footer.termsOfService', 'Terms of Service')}
                </a>
              </Link>
            </nav>
          </Row>
        </div>
      </Wrapper>
    </footer>
  )
}

export default BusinessPageFooter
